'use client';

import { APP_SCORING_PERCENTAGE_RANGES } from '@/constants';
import { ISimulationScore, TSimulationType } from '@/types';

// ================================
// Constants
// ================================

/**
 * Current passing threshold used for certification/practice pass evaluation.
 *
 * Kept aligned with existing scoring behavior to avoid regressions during Phase 1.
 */
export const DEFAULT_PASSING_PERCENTAGE = APP_SCORING_PERCENTAGE_RANGES.L4[0]; // 90%

// ================================
// Public Domain Policy
// ================================

/**
 * Determines if pass/fail evaluation applies to a simulation type.
 */
export const isPassApplicable = (simulationType?: TSimulationType): boolean => {
  return simulationType === 'certification' || simulationType === 'practice';
};

/**
 * Evaluates pass/fail for scoring contexts where pass semantics are applicable.
 *
 * Behavior is intentionally parity-safe with the existing utility implementation:
 * 1. Non-applicable simulation types return `null`.
 * 2. Explicit API certPass boolean overrides all other inputs.
 * 3. score.pass boolean overrides percentage computation.
 * 4. Numeric score/scoreMax fallback uses >= DEFAULT_PASSING_PERCENTAGE.
 * 5. scoreMax=0 with non-zero score returns `null`.
 */
export const evaluateScorePass = ({
  certPass,
  score,
  simulationType,
}: {
  certPass?: boolean;
  score?: ISimulationScore | null;
  simulationType?: TSimulationType;
}): boolean | null => {
  // ── Step 1: Simulation type guard ────────────────────────────────────────
  // Pass/fail is only applicable to 'certification' and 'practice' simulations.
  // 'role-play' sessions are unscored by design, and any unknown future types
  // should also be treated as non-applicable until explicitly supported.
  if (!isPassApplicable(simulationType)) {
    return null;
  }

  // ── Step 2: Explicit API verdict ─────────────────────────────────────────
  // When the backend has already computed and returned a pass/fail result,
  // trust it directly rather than re-deriving it from the raw score.
  // The strict `typeof` check prevents non-boolean values (e.g. 1, 0, "true")
  // from being treated as a pass/fail verdict — those fall through to later steps.
  if (typeof certPass === 'boolean') {
    return certPass;
  }

  // ── Step 3: Score object presence check ──────────────────────────────────
  // Without a score object there is no data to evaluate. Both `undefined` and
  // `null` are handled here; the redundant `score === null` check mirrors the
  // original code's intent for clarity alongside the truthiness check.
  if (!score || score === null) {
    return null;
  }

  // ── Step 4: Explicit pass field on the score object ──────────────────────
  // Some score objects include a pre-computed `pass` boolean. When present
  // (i.e. not null and not undefined), prefer it over the percentage fallback
  // to avoid recalculating a result the backend already provided.
  if (score.pass != null) {
    return score.pass;
  }

  // ── Step 5: Percentage-based calculation ─────────────────────────────────
  // Final fallback: derive pass/fail from the numeric score and its maximum.
  // Both fields must be numbers (not null, undefined, NaN is still a number
  // and is handled by the >= comparison returning false).
  if (typeof score.score === 'number' && typeof score.scoreMax === 'number') {
    // Guard against division-by-zero when scoreMax is 0 but the score itself
    // is non-zero — this would produce Infinity, which should be treated as
    // indeterminate rather than an automatic pass.
    if (score.scoreMax === 0 && score.score !== 0) {
      return null;
    }

    // Compute the percentage and compare to the 70% passing threshold.
    // Note: edge cases such as 0/0 (NaN), negative values, and scores exceeding
    // scoreMax are all left to normal arithmetic so the comparison still produces
    // a determinate boolean in those scenarios.
    const percentage = (score.score / score.scoreMax) * 100;
    return percentage >= DEFAULT_PASSING_PERCENTAGE;
  }

  // ── Step 6: Indeterminate ─────────────────────────────────────────────────
  // Reached only when the score object exists but neither `score.pass` nor
  // both numeric score/scoreMax fields are available (e.g. partially populated
  // score objects returned by the API before processing is complete).
  return null;
};

/**
 * Backward-compatible positional-arg wrapper in the domain layer.
 *
 * This preserves the long-lived call-site signature while removing dependence
 * on the utility-layer `get-score-pass` wrapper.
 */
export const getScorePass = (
  certPass?: boolean,
  simulationType?: TSimulationType,
  score?: ISimulationScore | null,
): boolean | null => {
  return evaluateScorePass({ certPass, score, simulationType });
};

/**
 * Returns true if any score in the provided history array constitutes a pass
 * for the given simulation type.
 *
 * This is the canonical "has any passing attempt" summary helper. Use it instead
 * of independently calling `getScorePass` inside `.some()` at the call site.
 */
export const hasAnyPassingAttempt = (
  history: ISimulationScore[],
  simulationType?: TSimulationType,
): boolean => {
  return history.some((score) => getScorePass(undefined, simulationType, score) === true);
};
